import { useDispatch, useSelector } from "react-redux";
import Select, { OnChangeValue, ActionMeta, StylesConfig } from "react-select";
import { useEffect, useState } from "react";
import {
    AvailableOption,
    OptionEntityFilterParameter,
    DEFAULT_OPTION,
} from "../../../../models/viz/filters/OptionEntityFilter";
import { ApplicationState } from "../../../../store";
import { ActivateViewFilterAction, DeactivateViewFilterAction } from "../../../../store/analytics/Analytics";

const STYLES: StylesConfig<AvailableOption, true> = {
    multiValueRemove: (base, state) => {
        return state.data.isFixed ? { ...base, display: "none" } : base;
    },
    // Extra padding to account for hidden remove button
    multiValueLabel: (base, state) => {
        return state.data.isFixed ? { ...base, paddingRight: 6 } : base;
    },
    option: (base, state) => {
        return state.data.isHidden ? { ...base, display: "none" } : base;
    },
};

const EXCEPT_PREFIX = "except_";

export interface OptionFilterProps {
    viewId: string;
    filterId: string;
    availableOptions: AvailableOption[];
}

function createAllExceptOption(option: AvailableOption) {
    return {
        value: `${EXCEPT_PREFIX}${option.value}`,
        label: `All (except ${option.label})`,
        isFixed: true,
        defaultExcluded: false,
        isHidden: false,
    };
}

export function createSelectedOptions(
    baseOptions: readonly AvailableOption[],
    availableOptions: readonly AvailableOption[],
) {
    let selectedOptions = [...baseOptions].filter((o) => o.value != DEFAULT_OPTION.value);
    const realOptions = availableOptions.filter((o) => o.value != DEFAULT_OPTION.value);

    // Remove all except option if it is selected and replace it with the collection of options it represents
    const allExceptOption = selectedOptions.find((o) => o.value.startsWith(EXCEPT_PREFIX));
    if (allExceptOption) {
        const unselectedOption = availableOptions.find(
            (o) => o.value == allExceptOption.value.substring(EXCEPT_PREFIX.length),
        );

        if (unselectedOption) {
            selectedOptions = selectedOptions.filter((o) => o.value != allExceptOption.value);

            selectedOptions = [
                ...selectedOptions,
                ...availableOptions.filter((o) => o.value != DEFAULT_OPTION.value && o != unselectedOption),
            ];
        }
    }

    // if no options are selected, select the default option
    // if all options are selected, select the default option
    if (selectedOptions.length == 0 || selectedOptions.length == realOptions.length) {
        selectedOptions = [DEFAULT_OPTION];
    }

    // if all but one option is selected, create an all except option instead
    if (realOptions.length > 2 && selectedOptions.length == realOptions.length - 1) {
        const unselectedOption = availableOptions.find(
            (o) => o.value != DEFAULT_OPTION.value && !selectedOptions.some((selected) => selected.value == o.value),
        );

        if (unselectedOption) {
            selectedOptions = [createAllExceptOption(unselectedOption)];
        }
    }

    return selectedOptions;
}

function OptionFilter({ viewId, filterId, availableOptions }: OptionFilterProps) {
    const dispatch = useDispatch();
    const filterParams = useSelector(
        (state: ApplicationState) => state.analytics.views[viewId].filters[filterId]?.params,
    ) as OptionEntityFilterParameter;

    const isActive = useSelector(
        (state: ApplicationState) => state.analytics.views[viewId].filters[filterId]?.isActive ?? false,
    );

    const [selectedFilterOptions, setSelectedFilterOptions] = useState<readonly AvailableOption[]>(() =>
        createSelectedOptions(
            availableOptions.filter(
                (o: AvailableOption) =>
                    (o.value == DEFAULT_OPTION.value && filterParams?.options?.length == 0) ||
                    (filterParams?.options?.includes(o.value) ?? false),
            ),
            availableOptions,
        ),
    );

    const [options, setOptions] = useState<readonly AvailableOption[]>([]);
    const [isSyncingFromRedux, setIsSyncingFromRedux] = useState(false);

    // Sync with Redux store when filter params change
    useEffect(() => {
        if (isActive && filterParams?.options) {
            const newSelectedOptions = createSelectedOptions(
                availableOptions.filter(
                    (o: AvailableOption) =>
                        (o.value == DEFAULT_OPTION.value && filterParams.options.length == 0) ||
                        filterParams.options.includes(o.value),
                ),
                availableOptions,
            );

            // Only update if the selection actually changed
            const currentValues = selectedFilterOptions
                .map((o) => o.value)
                .sort()
                .join(",");
            const newValues = newSelectedOptions
                .map((o) => o.value)
                .sort()
                .join(",");

            if (currentValues !== newValues) {
                setIsSyncingFromRedux(true);
                setSelectedFilterOptions(newSelectedOptions);
            }
        } else if (!isActive) {
            const currentValues = selectedFilterOptions
                .map((o) => o.value)
                .sort()
                .join(",");
            if (currentValues !== DEFAULT_OPTION.value) {
                setIsSyncingFromRedux(true);
                setSelectedFilterOptions([DEFAULT_OPTION]);
            }
        }
    }, [filterParams, isActive, availableOptions]);

    useEffect(() => {
        // Skip dispatching if we're syncing from Redux
        if (isSyncingFromRedux) {
            setIsSyncingFromRedux(false);
            return;
        }

        let selectedValues: string[] = [];

        const allExceptOption = selectedFilterOptions.find((o) => o.value.startsWith(EXCEPT_PREFIX));
        if (allExceptOption) {
            const unselectedOption = availableOptions.find(
                (o) => o.value == allExceptOption.value.substring(EXCEPT_PREFIX.length),
            );

            for (const option of availableOptions) {
                if (option.value != DEFAULT_OPTION.value && option != unselectedOption) {
                    option.isHidden = true;
                }
            }

            selectedValues = availableOptions
                .filter((o) => o.value != DEFAULT_OPTION.value && o != unselectedOption)
                .map((o) => o.value);
        } else {
            for (const option of availableOptions) {
                option.isHidden = false;
            }

            selectedValues = selectedFilterOptions.map((o) => o.value);
        }

        setOptions(selectedFilterOptions);

        if (selectedValues.length == 1 && selectedValues[0] == DEFAULT_OPTION.value) {
            dispatch({
                type: "analytics/deactivateViewFilter",
                payload: { viewId, filterId },
            } as DeactivateViewFilterAction);
        } else {
            dispatch({
                type: "analytics/activateViewFilter",
                payload: { viewId, filterId, parameter: { options: selectedValues } },
            } as ActivateViewFilterAction);
        }
    }, [selectedFilterOptions, isSyncingFromRedux]);

    const onOptionChange = (
        selectedOptions: OnChangeValue<AvailableOption, true>,
        actionMeta: ActionMeta<AvailableOption>,
    ) => {
        if (actionMeta.action == "select-option" && actionMeta.option.value == DEFAULT_OPTION.value) {
            selectedOptions = [DEFAULT_OPTION];
        } else {
            selectedOptions = createSelectedOptions(selectedOptions as readonly AvailableOption[], availableOptions);
        }

        setSelectedFilterOptions(selectedOptions);
    };

    return (
        <div>
            <Select
                isMulti
                classNamePrefix="react-select"
                value={options}
                options={availableOptions}
                isClearable={options.some((o) => !o.isFixed)}
                filterOption={(option) => !option.data.isHidden}
                onChange={onOptionChange}
                styles={STYLES}
            />
        </div>
    );
}

export default OptionFilter;
